import React, { useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Pie } from "react-chartjs-2";
import "./newDashboard.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

interface WidgetItem {
  label: string;
  value: string;
  color: string;
  unit?: string;
  legendName?: string | null;
}

interface DashboardWidgetProps {
  widgetTitle: string;
  widgetType: any;
  widgetItems: WidgetItem[];
}

const unitMultipliers: any = {
  K: 1_000,     
  L: 100_000,    
  Cr: 10_000_000,
};

const ManagementDashboard: React.FC<DashboardWidgetProps> = ({
  widgetTitle,
  widgetType,
  widgetItems,
}) => {
  // const total = useMemo(() => {
  //   if (widgetType === "pie") {
  //     return widgetItems?.reduce((sum, item) => {
  //       const value = parseFloat(item.value) || 0;
  //       return sum + value;
  //     }, 0);
  //   }
  //   return null;
  // }, [widgetItems, widgetType]);

  const total = useMemo(() => {
  if (widgetType === "pie") {
    const totalInBaseUnit = widgetItems?.reduce((sum, item) => {
      const value = parseFloat(item.value) || 0;
      const multiplier = item?.unit ? unitMultipliers[item.unit] : 1;
      return sum + value * multiplier;
    }, 0);

    if (totalInBaseUnit === 0) return { value: 0, unit: '' };

    let displayUnit = 'K';
    let displayValue = totalInBaseUnit / 1_000;

    if (totalInBaseUnit >= 1_00_000 && totalInBaseUnit < 1_00_00_000) {
      displayUnit = 'L';
      displayValue = totalInBaseUnit / 1_00_000;
    } else if (totalInBaseUnit >= 1_00_00_000) {
      displayUnit = 'Cr';
      displayValue = totalInBaseUnit / 1_00_00_000;
    }

    return {
      value: parseFloat(displayValue.toFixed(2)),
      unit: displayUnit,
    };
  }

  return null;
}, [widgetItems, widgetType]);

  const chartData = useMemo(() => {
    const labels = widgetItems?.map(
      (item) =>
        `${item.label}${item.unit ? " " + item?.value + " " + item.unit : ""}`
    );
    const values = widgetItems?.map((item) => parseFloat(item?.value));
    const colors = widgetItems?.map((item) => item?.color);

    if (widgetType === "pie") {
      return {
        labels,
        datasets: [
          {
            data: values,
            backgroundColor: colors,
          },
        ],
      };
    }

    if (widgetType === "bar stacked") {
      const labels = Array.from(
        new Set(widgetItems?.map((item) => item?.label))
      );

      const legendGroups: Record<string, WidgetItem[]> = {};
      widgetItems?.forEach((item) => {
        if (!legendGroups[item?.legendName || "Other"]) {
          legendGroups[item?.legendName || "Other"] = [];
        }
        legendGroups[item?.legendName || "Other"].push(item);
      });

      const datasets = Object.keys(legendGroups)?.map((legend) => {
        const items = legendGroups[legend];
        return {
          label: legend,
          data: labels?.map((month) => {
            const found = items?.find((i) => i?.label === month);
            return found ? parseFloat(found.value) : 0;
          }),
          backgroundColor: items[0]?.color || "#999",
          borderRadius: 6,
          barThickness: 60,
        };
      });

      return { labels, datasets };
    }

    // bar
    return {
      labels,
      datasets: [
        {
          label: widgetTitle,
          data: values,
          backgroundColor: colors,
          borderRadius: 8,
          barThickness: 60,
        },
      ],
    };
  }, [widgetItems, widgetType, widgetTitle]);

  const options: any = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: {
        duration: 900,
        easing: "easeOutQuart",
      },
      plugins: {
        tooltip: {
          callbacks: {
            label: (context: any) => {
              let label = context.label || "";
              let value = context.raw;
              return `${label}: ${value}`;
            },
          },
        },
        legend: {
          display: true,
          position: "top" as const,
          labels: {
            color: "black",
            font: {
              size: 16,
              weight: "bold",
            },
          },
        },
        title: {
          display: true,
          text: widgetTitle,
          color: "rgb(0, 76, 143)",
          font: {
            size: 24,
          },
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
          ticks: {
            color: "black",
            font: {
              size: 12,
              weight: "bold",
            },
          },
        },
        y: {
          grid: {
            display: false,
          },
          ticks: {
            color: "black",
            font: {
              size: 12,
              weight: "bold",
            },
          },
        },
      },
    }),
    [widgetTitle]
  );

  return (
    <div className="dashboard-card">
      {widgetType === "bar" ? (
        <Bar
          data={chartData}
          options={{
            ...options,
            plugins: {
              ...options.plugins,
              tooltip: {
                callbacks: {
                  label: (context: any) => {
                    let label = context.label || "";
                    let value = context.raw;
                    return `${label}`;
                  },
                },
              },
            },
          }}
        />
      ) : widgetType === "bar stacked" ? (
        <Bar
          data={chartData}
          options={{
            ...options,
            plugins: {
              ...options.plugins,
              tooltip: {
                callbacks: {
                  label: (context: any) => {
                    const label = context.label || "";
                    const value = context.raw;
                    const legend = context.dataset.label;
                    const item = widgetItems.find(
                      (i) =>
                        i.label === label &&
                        (i.legendName || "Other") === legend
                    );
                    const unit = item?.unit ? ` ${item.unit}` : "";
                    return `${label}: ${value}${unit}`;
                  },
                },
              },
            },
            scales: {
              x: {
                ...options.scales.x,
                stacked: true,
              },
              y: {
                ...options.scales.y,
                stacked: true,
              },
            },
          }}
        />
      ) : widgetType === "bar horizontal" ? (
        <Bar
          data={chartData}
          options={{
            ...options,
            indexAxis: "y",
          }}
        />
      ) : (
        <div style={{ position: "relative", width: "100%", height: "100%" }}>
          <Pie
            data={chartData}
            options={{
              ...options,
              scales: {
                ...options.scales,
                x: {
                  display: false,
                },
                y: {
                  display: false,
                },
              },
              plugins: {
                ...options.plugins,
                tooltip: {
                  callbacks: {
                    label: (context: any) => {
                      let label = context.label || "";
                      let value = context.raw;
                      return `${label}`;
                    },
                  },
                },
              },
            }}
          />
          {total !== null && (
            <div
              style={{
                position: "absolute",
                top: "50%",
                right: "20px",
                transform: "translateY(-50%)",
                fontSize: "24px",
                fontWeight: "bold",
                color: "blue",
                backgroundColor: "white",
                padding: "8px 12px",
                borderRadius: "8px",
                boxShadow: "0 2px 4px rgba(84, 20, 234, 0.1)",
                border: "1px solid #e0e0e0",
              }}
            >
              ₹{total?.value.toFixed(2) + (total?.unit ? " " + total.unit : "")}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ManagementDashboard;
 